import React, { useState, useEffect, useRef, useCallback } from "react";
import styled from "styled-components";
import PropTypes from "prop-types";
import { ChevronLeft, ChevronRight } from "lucide-react";
import TranscriptionDisplay from "./TranscriptionDisplay";
import AIAssistant from "./AIAssistant";
import NotesManager from "./NotesManager";
import { useTheme } from "styled-components";
import { useDemo } from "../../contexts/DemoContext";
import DemoOverlay from "./DemoOverlay";
import { useLayout } from "../../contexts/LayoutContext";

const SidebarContainer = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  width: ${(props) => (props.$isExpanded ? "25%" : "0")};
  height: 100vh;
  display: flex;
  flex-direction: column;
  transition: width 0.2s ease;
  overflow: hidden;
  pointer-events: ${(p) => p.$dimSidebar ? "none" : "auto"};
  z-index: 40;
  background: ${(p) => p.theme.isDarkMode ? '#0f172a' : '#ffffff'};
  border-left: 1px solid ${(p) => p.theme.isDarkMode ? '#00ff9d30' : '#029831'};

  /* Remove any backdrop filters */
  backdrop-filter: none;
  -webkit-backdrop-filter: none;

  /* Ensure the sidebar content stays above overlays */
  & > * {
    z-index: 41;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    width: ${(props) => (props.$isExpanded ? "100%" : "0")};
  }
`;

const SidebarHeader = styled.div`
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  background: ${(p) => p.theme.isDarkMode ? '#0f172a' : '#ffffff'};
  min-height: var(--header-height);
  z-index: 2;
  color: ${(p) => p.theme.isDarkMode ? '#f8fafc' : '#1e293b'};
  padding: 8px 12px 0 12px;
  position: relative;
  border-bottom: none;
`;

const TabsContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  padding: 0 0.25rem;
  margin-bottom: 0.5rem;
  background: ${(p) => p.theme.isDarkMode ? '#0f172a' : '#ffffff'};
`;

const TabButton = styled.button`
  padding: 4px 18px;
  border-radius: 9999px;
  border: none;
  font-size: 0.92rem;
  font-weight: 500;
  pointer-events: ${(p) => (p.$isDemoMode || p.$disabled) ? "none" : "auto"};
  cursor: ${(p) => (p.$isDemoMode || p.$disabled) ? "not-allowed" : "pointer"};
  opacity: ${(p) => (p.$isDemoMode || p.$disabled) ? "0.7" : "1"};
  background: ${(p) => p.$isActive
    ? (p.theme.isDarkMode ? 'rgba(0,255,157,0.18)' : '#e6f8ed')
    : (p.theme.isDarkMode ? '#121726' : '#f9fafb')};
  color: ${(p) => p.$isActive
    ? (p.theme.isDarkMode ? '#00ff9d' : '#029831')
    : (p.theme.isDarkMode ? '#94a3b8' : '#64748b')};
  border: 1.5px solid transparent;
  box-shadow: ${(p) => p.$isActive 
    ? (p.theme.isDarkMode ? '0 0 0 2px #02983144' : '0 0 0 2px #02983144')
    : 'none'};
  transition: all 0.18s;

  &:hover {
    background: ${(p) => (!p.$isDemoMode && !p.$disabled) && (p.$isActive
      ? (p.theme.isDarkMode ? 'rgba(0,255,157,0.25)' : '#d6f5e5')
      : (p.theme.isDarkMode ? '#1e293b' : '#f1f5f9'))};
  }
`;

const TabBadge = styled.span`
  margin-left: 6px;
  background: ${({ theme }) => theme.isDarkMode ? '#00ff9d' : theme.colors.accent.main};
  color: ${({ theme }) => theme.isDarkMode ? '#0f172a' : '#fff'};
  font-size: 11px;
  padding: 1px 6px;
  border-radius: 8px;
  font-weight: 600;
`;

const HeaderDivider = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 2px;
  background: ${(p) => p.theme.isDarkMode 
    ? 'linear-gradient(90deg, #00ff9d 0%, #1e9cff 100%)'
    : '#029831'};
  box-shadow: ${(p) => p.theme.isDarkMode 
    ? '0 0 6px #00ff9d66, 0 0 12px #1e9cff22'
    : 'none'};
  border-radius: 2px;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  background: none;
  border: none;
  color: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.4)'};
  cursor: pointer;
  padding: 4px;
  line-height: 1;
  font-size: 18px;
  border-radius: 4px;
  
  &:hover {
    color: ${({ theme }) => theme.isDarkMode ? '#fff' : 'rgba(0, 0, 0, 0.8)'};
    background: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'};
  }
`;

const ReminderToast = styled.div`
  position: absolute;
  bottom: 100px;
  left: 50%;
  transform: translateX(-50%);
  background: ${({ theme }) => theme.isDarkMode 
    ? 'linear-gradient(135deg, #1a1f35, #2d364d)'
    : 'linear-gradient(135deg, #ffffff, #f8fafc)'};
  color: ${({ theme }) => theme.isDarkMode ? '#fff' : theme.colors.neutral[900]};
  padding: 20px 32px;
  border-radius: 12px;
  font-size: 16px;
  min-width: 280px;
  box-shadow: ${({ theme }) => theme.isDarkMode 
    ? '0 8px 32px rgba(0, 0, 0, 0.4)' 
    : '0 8px 32px rgba(0, 0, 0, 0.1)'};
  border: 1px solid ${({ theme }) => theme.isDarkMode 
    ? 'rgba(255, 255, 255, 0.1)' 
    : 'rgba(0, 0, 0, 0.05)'};
  z-index: 1000;
  display: flex;
  flex-direction: column;
  gap: 12px;
  animation: slideUp 0.3s ease-out;

  @keyframes slideUp {
    from {
      transform: translate(-50%, 20px);
      opacity: 0;
    }
    to {
      transform: translate(-50%, 0);
      opacity: 1;
    }
  }
`;

const ToastTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-weight: 600;
  font-size: 18px;
  color: ${({ theme }) => theme.isDarkMode 
    ? '#19fa6c' 
    : theme.colors.primary.main};
`;

const ToastMessage = styled.div`
  color: ${({ theme }) => theme.isDarkMode ? '#fff' : theme.colors.neutral[700]};
  font-size: 14px;
`;

const ToastAction = styled.button`
  background: none;
  border: none;
  padding: 0;
  color: ${({ theme }) => theme.isDarkMode 
    ? '#19fa6c' 
    : theme.colors.primary.main};
  font-size: 14px;
  cursor: pointer;
  text-decoration: underline;
  opacity: 0.9;
  transition: opacity 0.2s;

  &:hover {
    opacity: 1;
  }
`;

// TabButton styled component moved to top of file

const ToggleButtonContainer = styled.div`
  position: fixed;
  right: ${(p) =>
        p.$isExpanded
          ? "calc(var(--right-sidebar-width) - 6px)"
          : "0"};
  top: 50%;
  transform: translate(50%);  /* Center both horizontally and vertically */
  z-index: 50;
  /* 24px wide when collapsed; 32px when expanded for a perfect 16px/16px split */
  width: ${(props) => (props.$isExpanded ? "32px" : "24px")};
  /* on hover, always pop out to 32px so the icon is fully visible */
  &:hover {
    width: 32px;
  }
  background-color: transparent !important; /* Force transparency without affecting other styles */
  pointer-events: ${(p) => p.$dimSidebar ? "none" : "auto"};
  box-shadow: none !important;
  backdrop-filter: none;
  -webkit-backdrop-filter: none;
  opacity: ${(p) => p.$dimSidebar ? "0.4" : "1"};
  cursor: ${(p) => p.$dimSidebar ? "not-allowed" : "pointer"};

  /* width still animates, but right snaps instantly into place */
  transition: width 0.2s ease-in-out;
`;

const ToggleButton = styled.button`
  width: 1.25rem;
  height: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(p) => p.theme.isDarkMode ? '#0f172a' : 'rgb(36, 43, 99)'};
  color: white;
  border: ${(p) => p.theme.isDarkMode ? '2px solid transparent' : '1px solid rgb(36, 43, 99)'};
  border-radius: 9999px;
  cursor: ${(p) => p.$blockToggle ? "not-allowed" : "pointer"};
  padding: 0;
  transition: all 0.2s ease;
  pointer-events: ${(p) => p.$dimSidebar ? "none" : "auto"};
  opacity: ${(p) => p.$dimSidebar ? "0.7" : "1"};
  position: relative;

  ${(p) => p.theme.isDarkMode && `
    background: linear-gradient(#0f172a, #0f172a) padding-box,
                linear-gradient(45deg, #131d80, #02b957, #00ff9d) border-box !important;
    box-shadow: 0 0 3px rgba(0, 255, 157, 0.13);
  `}

  &:hover {
    background: ${(p) => p.theme.isDarkMode ? '#0f172a' : 'rgb(36, 43, 99)'};
  }

  svg {
    width: 0.875rem;
    height: 0.875rem;
    stroke-width: 2;
  }


`;

const ContentContainer = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 8px 10px;
  background: ${(p) => p.theme.isDarkMode ? '#0f172a' : '#ffffff'};
  color: ${(p) => p.theme.isDarkMode ? '#e2e8f0' : p.theme.colors.neutral[900]};

  > div {
    background: ${(p) => p.theme.isDarkMode ? '#0f172a' : '#ffffff'};
  }

  .sc-dcJsrY, .sc-hmdomO, .sc-bXCLTC, .sc-jsJBEP {
    background: ${(p) => p.theme.isDarkMode ? '#0f172a' : '#ffffff'};
  }

  > * + * {
    margin-top: 0px;
  }

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: ${(p) => p.theme.isDarkMode ? '#1e293b' : '#f1f5f9'};
    border-radius: 2px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${(p) => p.theme.isDarkMode ? '#334155' : '#cbd5e1'};
    border-radius: 2px;
  }
`;

const TranscriptWrapper = styled.div`
  height: 100%;
  overflow: visible;
  pointer-events: ${(p) => p.$dimSidebar ? "none" : "auto"};
  opacity: ${(p) => p.$dimSidebar ? "0.7" : "1"};
`;

const TabContentWrapper = styled.div`
  pointer-events: ${(p) => p.$dimSidebar ? "none" : "auto"};
  user-select: ${(p) => p.$dimSidebar ? "none" : "auto"};
  opacity: ${(p) => p.$dimSidebar ? "0.7" : "1"};
`;

const SidebarTemplate = ({
  isExpanded,
  onToggle,
  disabled,
  transcript,
  isRecording,
  setIsRecording,
  isPaused,
  setIsPaused,
  onHighlightSummary,
  notesSummary,
  onNotesSummaryChange,
  notesUpdates,
  onNotesUpdatesChange,
  notesTasks,
  onNotesTasksChange,
  onNotesSave,
  onModalAction,
  currentModalType,
  activeModalData,
}) => {
  const { theme, isDarkMode } = useTheme();
  const { isDemoMode, stopDemo, lastAction, hasAcknowledgedDemo } = useDemo();
  const { measureRightSidebar } = useLayout();
  const [activeTab, setActiveTab] = useState("transcript");
  const [lastReminderId, setLastReminderId] = useState(null);
  const [followUpCount, setFollowUpCount] = useState(0);
  const [animateFollowUp, setAnimateFollowUp] = useState(false);
  const [showReminderToast, setShowReminderToast] = useState(false);
  const isTranscriptOnlyMode = currentModalType === "TRANSCRIPTION_INTRODUCTION";
  const [activeTags, setActiveTags] = useState([]);
  const blockSidebarToggle = isDemoMode;
  const rightSidebarRef = useRef(null);
  const [suggestions, setSuggestions] = useState([
    {
      title: "Client Risk Profile Update",
      description:
        "Based on recent market discussions, consider reviewing the client's risk tolerance profile.",
    },
    {
      title: "Portfolio Rebalancing Opportunity",
      description:
        "Current asset allocation has drifted from target. Review and suggest adjustments.",
    },
    {
      title: "Estate Planning Review",
      description:
        "Client mentioned family changes. Schedule a comprehensive estate planning review.",
    },
  ]);

  // Effect to handle reminder actions
  useEffect(() => {
    console.log("🔔 [SidebarEffect]", {
      activeTab,
      lastAction,
      lastReminderId,
    });

    if (
      lastAction?.type === "ADD_REMINDER" &&
      lastAction.id !== lastReminderId
    ) {
      // Always bump the AI badge regardless of which tab is active
      console.log("🔔 Detected ADD_REMINDER → bumping badge (id:", lastAction.id, ")");
      setLastReminderId(lastAction.id);
      setFollowUpCount(c => c + 1);
      setAnimateFollowUp(true);
      setTimeout(() => setAnimateFollowUp(false), 800);
      
      // Show a quick toast in all tabs
      setShowReminderToast(true);
      setTimeout(() => setShowReminderToast(false), 5000);
    }
  }, [lastAction, lastReminderId]);

  // Add debug logs for state changes
  useEffect(() => {
    console.log('🎯 [Sidebar] State changed:', {
      isExpanded,
      isPaused,
      currentModalType,
      isTranscriptOnlyMode: false,
      isDemoMode
    });
  }, [isExpanded, isPaused, currentModalType, isDemoMode]);

  // Add debug logging for demo state
  useEffect(() => {
    console.log('🎯 SidebarTemplate demo state:', {
      isDemoMode,
      isRecording,
      isPaused,
      currentModalType,
      hasActiveModal: Boolean(currentModalType) || Boolean(activeModalData)
    });
  }, [isDemoMode, isRecording, isPaused, currentModalType, activeModalData]);

  // Prevent manual collapse during demo mode
  const handleToggleClick = useCallback(() => {
    if (isDemoMode) {
      console.log('🎯 [Sidebar] Toggle prevented in demo mode');
      return;
    }
    onToggle();
  }, [isDemoMode, onToggle]);

  // Only handle auto-expand for demo mode
  // Expected demo UX: keep sidebar closed until the meeting actually starts (Call Now → recording begins).
  // Also guard against React.StrictMode double-running effects in dev.
  const didAutoExpandRef = useRef(false);
  useEffect(() => {
    // Reset between demo sessions
    if (!isDemoMode) {
      didAutoExpandRef.current = false;
      return;
    }

    // Only auto-open once we are recording (i.e., after Call Now / Start Recording)
    if (isDemoMode && isRecording && !isExpanded && !didAutoExpandRef.current) {
      didAutoExpandRef.current = true;
      console.log('🎯 [Sidebar] Auto-expanding on demo recording start');
      onToggle();
    }
  }, [isDemoMode, isRecording, isExpanded, onToggle]);

  const ref = useRef();

  useEffect(() => {
    if (!ref.current) return;
    const cs = window.getComputedStyle(ref.current);
    console.log(
      '[Sidebar DEBUG]',
      'demoActive=', isDemoMode,
      'zIndex=', cs.zIndex,
      'pointerEvents=', cs.pointerEvents,
      'isExpanded=', isExpanded,
      'isPaused=', isPaused
    );
  }, [isDemoMode, isExpanded, isPaused]);

  // Measure after width transition (only once sidebar is done animating)
  useEffect(() => {
    const node = rightSidebarRef.current;
    if (!node) return;
    
    const handleTransitionEnd = (e) => {
      if (e.propertyName === "width") {
        measureRightSidebar(node);
      }
    };
    
    node.addEventListener("transitionend", handleTransitionEnd);
    return () => node.removeEventListener("transitionend", handleTransitionEnd);
  }, [measureRightSidebar, isExpanded]);

  // Always measure on mount and when expanded state changes
  useEffect(() => {
    const node = rightSidebarRef.current;
    if (node) measureRightSidebar(node);
  }, [measureRightSidebar, isExpanded]);

  // Debug effect for width changes - keep this for monitoring
  useEffect(() => {
    if (!ref.current) return;
    const width = ref.current.getBoundingClientRect().width;
    const style = getComputedStyle(document.documentElement);
    console.log('📏 [SidebarTemplate] Width measurement:', {
      isExpanded,
      width,
      rightSidebarWidth: style.getPropertyValue('--right-sidebar-width'),
      expectedWidth: isExpanded ? 'calc(25%)' : 0,
      computedStyle: {
        width: window.getComputedStyle(ref.current).width,
        display: window.getComputedStyle(ref.current).display,
        position: window.getComputedStyle(ref.current).position,
        right: window.getComputedStyle(ref.current).right,
      }
    });
  }, [isExpanded]);

  // Remove any setTimeout-based measurements as they're no longer needed
  // The transitionend event will handle final measurements reliably

  // Check if any modal is active
  const hasActiveModal = Boolean(currentModalType) || Boolean(activeModalData);
  
  // Single source of truth for sidebar protection - only dim during AI_SIDEBAR_INTRODUCTION while recording
  const isSidebarProtected = isDemoMode && isRecording && currentModalType === "AI_SIDEBAR_INTRODUCTION";
  const dimSidebar = isSidebarProtected;

  // Add comprehensive theme and layout debugging
  useEffect(() => {
    if (!ref.current) return;

    const logLayoutState = () => {
      const root = document.documentElement;
      const rootStyle = getComputedStyle(root);
      const sidebarStyle = getComputedStyle(ref.current);
      const toggleContainer = document.querySelector('[data-testid="sidebar-toggle-container"]');
      const toggleButton = document.querySelector('[data-testid="sidebar-toggle-button"]');

      console.group('🔍 [SidebarTemplate] Layout Debug');
      console.log('Theme:', { isDarkMode });
      console.log('State:', { isExpanded, isPaused, dimSidebar });
      
      // Log CSS Variables
      console.group('CSS Variables');
      console.log('--right-sidebar-width:', rootStyle.getPropertyValue('--right-sidebar-width'));
      console.log('--header-height:', rootStyle.getPropertyValue('--header-height'));
      console.log('--left-sidebar-width:', rootStyle.getPropertyValue('--left-sidebar-width'));
      console.groupEnd();

      // Log Sidebar Container Styles
      console.group('Sidebar Container');
      console.log('width:', sidebarStyle.width);
      console.log('position:', sidebarStyle.position);
      console.log('right:', sidebarStyle.right);
      console.log('zIndex:', sidebarStyle.zIndex);
      console.log('transform:', sidebarStyle.transform);
      console.log('pointerEvents:', sidebarStyle.pointerEvents);
      console.log('opacity:', sidebarStyle.opacity);
      console.groupEnd();

      // Log Toggle Button Container Styles
      if (toggleContainer) {
        console.group('Toggle Container');
        const toggleContainerStyle = getComputedStyle(toggleContainer);
        console.log('position:', toggleContainerStyle.position);
        console.log('right:', toggleContainerStyle.right);
        console.log('zIndex:', toggleContainerStyle.zIndex);
        console.log('transform:', toggleContainerStyle.transform);
        console.log('width:', toggleContainerStyle.width);
        console.groupEnd();
      }

      // Log Toggle Button Styles
      if (toggleButton) {
        console.group('Toggle Button');
        const toggleButtonStyle = getComputedStyle(toggleButton);
        console.log('background:', toggleButtonStyle.background);
        console.log('color:', toggleButtonStyle.color);
        console.log('pointerEvents:', toggleButtonStyle.pointerEvents);
        console.log('cursor:', toggleButtonStyle.cursor);
        console.groupEnd();
      }

      // Log Measurements
      console.group('Measurements');
      const rect = ref.current.getBoundingClientRect();
      console.log('BoundingClientRect:', {
        width: rect.width,
        height: rect.height,
        right: rect.right,
        left: rect.left
      });
      console.groupEnd();

      console.groupEnd();
    };

    // Log immediately
    logLayoutState();

    // Log after a short delay to catch any async updates
    const timeoutId = setTimeout(async () => {
      logLayoutState();
      // Force a resize event to see if it fixes layout
      window.dispatchEvent(new Event('resize'));
      // Log one more time after resize
      setTimeout(logLayoutState, 50);
    }, 200);

    return () => clearTimeout(timeoutId);
  }, [isDarkMode, isExpanded, isPaused, dimSidebar]);

  // Debug logging for dimSidebar state
  useEffect(() => {
    console.log('[Debug dimSidebar]', {
      isDemoMode,
      isRecording,
      currentModalType,
      isSidebarProtected,
      dimSidebar,
      hasActiveModal,
      activeModalData
    });
  }, [isDemoMode, isRecording, currentModalType, isSidebarProtected, dimSidebar, hasActiveModal, activeModalData]);

  const handleTagToggle = (tagId) => {
    const newActiveTags = activeTags.includes(tagId)
      ? activeTags.filter((t) => t !== tagId)
      : [...activeTags, tagId];

    setActiveTags(newActiveTags);

    // Filter suggestions based on active tags
    if (newActiveTags.length === 0) {
      setSuggestions([
        {
          title: "Client Risk Profile Update",
          description:
            "Based on recent market discussions, consider reviewing the client's risk tolerance profile.",
        },
        {
          title: "Portfolio Rebalancing Opportunity",
          description:
            "Current asset allocation has drifted from target. Review and suggest adjustments.",
        },
        {
          title: "Estate Planning Review",
          description:
            "Client mentioned family changes. Schedule a comprehensive estate planning review.",
        },
      ]);
    } else {
      // Filter suggestions based on active tags
      const tagBasedSuggestions = {
        risk: [
          {
            title: "Risk Tolerance Assessment",
            description:
              "Complete a new risk tolerance questionnaire to update client preferences.",
          },
          {
            title: "Market Volatility Review",
            description:
              "Discuss recent market changes and assess comfort with current strategy.",
          },
        ],
        goals: [
          {
            title: "Goal Progress Check",
            description:
              "Review progress towards retirement and education funding goals.",
          },
          {
            title: "New Goal Setting",
            description:
              "Client mentioned interest in vacation home. Schedule goal-setting session.",
          },
        ],
        estate: [
          {
            title: "Estate Plan Update",
            description:
              "Review and update beneficiary designations and trust arrangements.",
          },
          {
            title: "Legacy Planning",
            description:
              "Discuss charitable giving and inheritance strategies.",
          },
        ],
      };

      const filteredSuggestions = newActiveTags.flatMap(
        (tag) => tagBasedSuggestions[tag] || [],
      );

      setSuggestions(filteredSuggestions);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    // Handle different types of suggestions
    if (suggestion.title.toLowerCase().includes("risk")) {
      onModalAction?.("risk-assessment", { title: suggestion.title });
    } else if (suggestion.title.toLowerCase().includes("estate")) {
      onModalAction?.("estate-planning", { title: suggestion.title });
    } else if (suggestion.title.toLowerCase().includes("goal")) {
      onModalAction?.("goal-setting", { title: suggestion.title });
    } else {
      onModalAction?.("generic", {
        title: suggestion.title,
        description: suggestion.description,
      });
    }

    // Add a note about the action taken
    onNotesSave?.(
      `AI Assistant Action: ${suggestion.title}\n${suggestion.description}`,
    );
  };

  const handlePauseToggle = useCallback(() => {
    console.log('🎯 Toggling pause state:', !isPaused);
    setIsPaused(!isPaused);
    // If we're resuming, make sure recording is on
    if (isPaused) {
      setIsRecording(true);
    }
  }, [isPaused, setIsPaused, setIsRecording]);

  // Add a wrapper for stopDemo that ensures we pause first
  const handleStopDemo = useCallback(() => {
    console.log('🎯 Stopping demo from SidebarTemplate');
    // Force pause state
    setIsPaused(true);
    // Small delay to ensure pause takes effect
    setTimeout(async () => {
      stopDemo();
    }, 100);
  }, [setIsPaused, stopDemo]);

  // Comprehensive event trap for demo mode
  const handleEventTrap = useCallback((e) => {
    if (isDemoMode && (!isPaused || !isRecording)) {
      console.log('🛑 [SidebarTemplate] Trapping event:', {
        type: e.type,
        target: e.target,
        currentTarget: e.currentTarget,
        isPaused,
        path: e.composedPath?.()?.map(el => ({
          className: el.className,
          id: el.id,
          tagName: el.tagName
        }))
      });

      // Hard-stop propagation at multiple levels
      e.preventDefault();
      e.stopPropagation();
      // Stop other listeners on the same element
      e.stopImmediatePropagation?.();
      // Cancel any default behaviors
      e.cancelBubble = true;
      e.returnValue = false;
      return false;
    }
  }, [isDemoMode, isPaused]);

  // Add a wrapper for any interactive handlers
  const wrapInteractiveHandler = useCallback((handler) => (e) => {
    if (isDemoMode && !isPaused) {
      handleEventTrap(e);
      return;
    }
    handler?.(e);
  }, [isDemoMode, isPaused, handleEventTrap]);

  return (
    <>
      {isDemoMode && isRecording && (
        <DemoOverlay
          isRecording={isRecording}
          isPaused={isPaused}
          onPauseToggle={handlePauseToggle}
          onStop={stopDemo}
          hasActiveModal={hasActiveModal}
          sidebarExpanded={isExpanded}
        />
      )}
      <SidebarContainer
        ref={rightSidebarRef}
        $isExpanded={isExpanded}
        $demoMode={isDemoMode}
        $isPaused={isPaused}
        $blockToggle={blockSidebarToggle}
        $dimSidebar={dimSidebar}
        className="sidebar-container"
        data-layout="right-sidebar"
        onClick={handleEventTrap}
        onMouseDown={handleEventTrap}
        onMouseUp={handleEventTrap}
        onPointerDown={handleEventTrap}
        onPointerUp={handleEventTrap}
      >
        <SidebarHeader>
          <TabsContainer>
            <TabButton
              $isActive={activeTab === "transcript"}
              onClick={wrapInteractiveHandler(() => setActiveTab("transcript"))}
              $isDemoMode={isDemoMode}
              $disabled={disabled}
            >
              Transcript
            </TabButton>
            {!isTranscriptOnlyMode && (
              <>
                <TabButton
                  $isActive={activeTab === "notes"}
                  onClick={wrapInteractiveHandler(() => setActiveTab("notes"))}
                  $isDemoMode={isDemoMode}
                  $disabled={disabled}
                >
                  Notes
                </TabButton>
                <TabButton
                  $isActive={activeTab === "ai"}
                  onClick={wrapInteractiveHandler(() => setActiveTab("ai"))}
                  $isDemoMode={isDemoMode}
                  $disabled={disabled}
                >
                  AI
                  {followUpCount > 0 && <TabBadge>{followUpCount}</TabBadge>}
                </TabButton>
              </>
            )}
          </TabsContainer>
          <HeaderDivider />
        </SidebarHeader>

        <ContentContainer>
          {showReminderToast && (
            <ReminderToast>
              <CloseButton onClick={wrapInteractiveHandler(() => setShowReminderToast(false))}>×</CloseButton>
              <ToastTitle>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z" />
                </svg>
                Reminder Added Successfully
              </ToastTitle>
              <ToastMessage>
                Your follow-up item has been added to the AI Assistant
              </ToastMessage>
              <ToastAction onClick={wrapInteractiveHandler(() => setActiveTab("ai"))}>
                Click to view in AI Assistant →
              </ToastAction>
            </ReminderToast>
          )}
          {activeTab === "transcript" && (
            <TranscriptWrapper 
              $dimSidebar={dimSidebar}
            >
              <TranscriptionDisplay
                transcript={transcript}
                isRecording={isRecording}
                setIsRecording={setIsRecording}
                isPaused={isPaused}
                setIsPaused={setIsPaused}
                onHighlightSummary={onHighlightSummary}
              />
            </TranscriptWrapper>
          )}

          {!isTranscriptOnlyMode && activeTab === "notes" && (
            <TabContentWrapper $dimSidebar={dimSidebar}>
              <NotesManager
                summary={notesSummary}
                onSummaryChange={onNotesSummaryChange}
                updates={notesUpdates}
                onUpdatesChange={onNotesUpdatesChange}
                tasks={notesTasks}
                onTasksChange={onNotesTasksChange}
                onSave={onNotesSave}
                readOnly={isDemoMode && !isPaused}
              />
            </TabContentWrapper>
          )}

          {!isTranscriptOnlyMode && activeTab === "ai" && (
            <TabContentWrapper $dimSidebar={dimSidebar}>
              <AIAssistant
                activeTags={activeTags}
                onTagToggle={handleTagToggle}
                suggestions={suggestions}
                onSuggestionClick={handleSuggestionClick}
                followUpCount={followUpCount}
                animateFollowUp={animateFollowUp}
              />
            </TabContentWrapper>
          )}
        </ContentContainer>
      </SidebarContainer>

      {/* Vertical Neon Line - Only in dark mode when expanded */}
      {isDarkMode && isExpanded && (
        <div
          className="fixed top-0 z-40 w-0.5 neon-divider-vertical"
          style={{
            right: 'calc(var(--right-sidebar-width) - 4px)',
            height: '100vh',
            transition: 'right 0.2s ease',
          }}
        />
      )}

      <ToggleButtonContainer 
        $isExpanded={isExpanded}
        $demoMode={isDemoMode}
        $isPaused={isPaused}
        $dimSidebar={dimSidebar}
        onClick={handleEventTrap}
        onMouseDown={handleEventTrap}
        data-testid="sidebar-toggle-container"
      >
        <ToggleButton
          onClick={wrapInteractiveHandler(handleToggleClick)}
          $isExpanded={isExpanded}
          $demoMode={isDemoMode}
          $isPaused={isPaused}
          $blockToggle={blockSidebarToggle}
          aria-label={isExpanded ? "Collapse sidebar" : "Expand sidebar"}
          title={isExpanded ? "Collapse sidebar" : "Expand sidebar"}
          data-testid="sidebar-toggle-button"
        >
          {isExpanded ? <ChevronRight /> : <ChevronLeft />}
        </ToggleButton>
      </ToggleButtonContainer>
    </>
  );
};

SidebarTemplate.propTypes = {
  isExpanded: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  transcript: PropTypes.array,
  isRecording: PropTypes.bool,
  setIsRecording: PropTypes.func,
  isPaused: PropTypes.bool,
  setIsPaused: PropTypes.func,
  onHighlightSummary: PropTypes.func,
  notesSummary: PropTypes.string,
  onNotesSummaryChange: PropTypes.func,
  notesUpdates: PropTypes.array,
  onNotesUpdatesChange: PropTypes.func,
  notesTasks: PropTypes.array,
  onNotesTasksChange: PropTypes.func,
  onNotesSave: PropTypes.func,
  onModalAction: PropTypes.func,
  currentModalType: PropTypes.string,
  activeModalData: PropTypes.object,
};

export default SidebarTemplate;
